import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid } from "recharts";
import { TrendingUp, TrendingDown, Minus, Loader2, ArrowRight, LineChart as LineIcon } from "lucide-react";

function ChangeBadge({ change }) {
  const Icon = change > 0 ? TrendingUp : change < 0 ? TrendingDown : Minus;
  const color = change > 0 ? "#00C853" : change < 0 ? "#FF3B30" : "#9CA3AF";
  return (
    <span className="inline-flex items-center gap-1 text-sm font-bold" style={{ color }}>
      <Icon className="w-4 h-4" />
      {change > 0 ? "+" : ""}
      {change}
    </span>
  );
}

export default function Trends() {
  const [data, setData] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/trends").then((r) => setData(r.data)).catch(() => setData([]));
  }, []);

  if (data === null)
    return (
      <div className="flex justify-center pt-20">
        <Loader2 className="w-8 h-8 animate-spin text-klein" />
      </div>
    );

  return (
    <div className="max-w-5xl mx-auto">
      <div className="mb-8">
        <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tighter">SEO Trends</h1>
        <p className="text-gray-500 mt-2">Track how each site's SEO score changes across your audits.</p>
      </div>

      {data.length === 0 ? (
        <div className="bg-white border border-gray-200 rounded-xl p-16 text-center" data-testid="trends-empty">
          <LineIcon className="w-12 h-12 mx-auto text-gray-300" strokeWidth={1.25} />
          <h3 className="font-heading font-bold text-xl mt-4">No data yet</h3>
          <p className="text-gray-500 mt-1 mb-6">Run audits on the same URL over time to see progress.</p>
          <Button onClick={() => navigate("/")} className="bg-[#0A0A0A] hover:bg-gray-800" data-testid="trends-run-audit">
            Run an audit <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      ) : (
        <div className="space-y-6">
          {data.map((site) => {
            const chartData = site.points.map((p) => ({
              date: new Date(p.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric" }),
              score: p.score,
            }));
            return (
              <div key={site.url} className="bg-white border border-gray-200 shadow-sm rounded-xl p-6" data-testid="trend-card">
                <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                  <div className="min-w-0">
                    <h3 className="font-semibold text-gray-900 truncate">{site.url}</h3>
                    <p className="text-xs text-gray-500 mt-0.5">{site.count} audit(s)</p>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <div className="uppercase-label">Latest</div>
                      <div className="font-heading text-2xl font-black">{site.latest_score}</div>
                    </div>
                    {site.count > 1 && <ChangeBadge change={site.change} />}
                  </div>
                </div>
                {site.count > 1 ? (
                  <ResponsiveContainer width="100%" height={180}>
                    <LineChart data={chartData} margin={{ left: -24, right: 8 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" vertical={false} />
                      <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#9CA3AF" }} axisLine={false} tickLine={false} />
                      <YAxis domain={[0, 100]} tick={{ fontSize: 11, fill: "#9CA3AF" }} axisLine={false} tickLine={false} />
                      <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #E5E7EB", fontSize: 13 }} />
                      <Line type="monotone" dataKey="score" stroke="#002FA7" strokeWidth={2.5} dot={{ r: 3, fill: "#002FA7" }} />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <p className="text-sm text-gray-400 italic">Run another audit on this URL to see a trend line.</p>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
